import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { connect } from 'redux-bundler-react';
import { createColumnHelper } from '@tanstack/react-table';
import { toast } from 'react-toastify';
import { mdiPlusBox, mdiCloseOctagon } from '@mdi/js';
import { Alert, Button } from '@trussworks/react-uswds';
import StepHeader from '@components/step-header/StepHeader';
import Icon from '@components/icon/Icon';
import AddAdjoiningPropertyOwnerModal from '@forms/components/modals/AddAdjoiningPropertyOwnerModal';
import AgentsActionsTableCell from '@src/app-components/table/tableCellComponents/AgentsActionsTableCell';
import statusModal from '@forms/components/modals/statusModal';
import { ContactTypes, ErrorMessages, ResourceFiles, FileTypes } from '@src/utils/enums';
import { parseCsvWithXlsx, validateCsvHeaders } from '@src/utils/helpers';
import { tError, tSuccess } from '@src/utils/toast-helpers';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';
import ErrorSummary from '@components/error-summary/ErrorSummary';
import FileInput from '@components/file-input/FileInput';
import TanStackTableBasic from '@src/app-components/table/TanStackTable/TanStackTableBasic';
import LinkButton from '@components/link/linkButton';
import _isEqual from 'lodash/isEqual';
import '@styles/_buttons.scss';
import { useRequest, useModal } from '@stores';
import { formatState, formatCountry, validateCSV } from '@forms/input-forms/helpers';
import { CsvValidationModal } from '../../components/modals/CsvValidationModal';
import { useContactsTable } from '@src/stores/contactsTable.store';
import { handleFileProcessing } from '@src/utils/fileProcessing';

export const PermitsFormAdjoiningPropertyOwnersMetadata = {
  sectionName: 'Adjoining Property Owners',
};

const PermitsFormAdjoiningPropertyOwners = connect(
  'doClearContactType',
  'doDownloadFile',
  'selectRequestAPIData',
  'selectPermitData',
  ({ doClearContactType, doDownloadFile, requestAPIData, permitData, stepNo }) => {
    const { openModal } = useModal();
    const saveContactsTable = useContactsTable((state) => state.saveContactsTable);
    const [rowData, setRowData] = useState([]);
    const prevRowDataRef = useRef([]);
    const [tableIsDirty, setTableIsDirty] = useState(false);
    const [stagedFile, setStagedFile] = useState([]);
    const [rowErrorIndices, setRowErrorIndices] = useState([]);
    const { openSecondaryModal } = useModal();
    const requestedPermitType = permitData?.requestedPermitType;
    const adjPropertyOwnersTableLength = rowData?.length;
    const isStandardPermit = permitData?.requestedPermitType === 'Standard Permit';

    const schema = (adjPropertyOwnersTableLength) =>
      yup.object().shape({
        adjPropertyOwnersTable: yup.boolean().when('propOwners', {
          is: (val) => val === 'true' && adjPropertyOwnersTableLength < 1,
          then: () =>
            yup.boolean().required('Adjoining Property Owners: At least one adjoining property owner is required'),
        }),
      });

    const defaultValues = {};

    const methods = useForm({
      resolver: yupResolver(schema(adjPropertyOwnersTableLength)),
      mode: 'onBlur',
      defaultValues: defaultValues,
    });
    const {
      formState: { errors },
      setError,
      trigger,
    } = methods;
    const { isFormReadOnly: isReadOnly, updateSection, isSectionTouched } = useRequest();

    const errorCount = Object.keys(errors).length;
    const isValid = Object.keys(errors).length === 0;
    const isDirty = tableIsDirty;
    const isTouched = isSectionTouched(stepNo);

    useEffect(() => {
      updateSection(stepNo, { dirty: isDirty, valid: isValid });
    }, [stepNo, isDirty, isValid, updateSection]);

    useEffect(() => {
      const tableDataChanged = !_isEqual(rowData, prevRowDataRef.current);
      setTableIsDirty(tableDataChanged);
    }, [rowData]);

    const columnHelper = createColumnHelper();

    const columnDefs = useMemo(
      () => [
        columnHelper.display({
          header: 'Actions',
          id: 'actions',
          cell: ({ row }) => (
            <AgentsActionsTableCell
              row={row}
              rowData={rowData}
              setRowData={setRowData}
              modalComponent={AddAdjoiningPropertyOwnerModal}
              isReadOnly={isReadOnly}
              contactType={ContactTypes.AdjoiningPropertyOwner}
            />
          ),
          size: 60,
          enableSorting: false,
          meta: {
            centerText: true,
          },
        }),
        columnHelper.accessor('firstName', {
          header: 'First Name',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
        }),
        columnHelper.accessor('middleName', {
          header: 'Middle Name',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          meta: {
            optional: true,
          },
        }),
        columnHelper.accessor('lastName', {
          header: 'Last Name',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
        }),
        columnHelper.accessor('address', {
          header: 'Address',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          size: 500,
        }),
        columnHelper.accessor('city', {
          header: 'City',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
        }),
        columnHelper.accessor('state', {
          header: 'State',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          size: 75,
        }),
        columnHelper.accessor('country', {
          header: 'Country',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          size: 75,
        }),
      ],
      [columnHelper, rowData, isReadOnly]
    );

    const clearRowData = useCallback(() => {
      setRowData([]);
      doClearContactType(ContactTypes.AdjoiningPropertyOwner);
    }, [doClearContactType]);

    useEffect(() => {
      // Load tables from database
      let adjoiningPropertyOwners = requestAPIData?.request?.contacts?.filter(
        (contact) => contact.contactType === ContactTypes.AdjoiningPropertyOwner
      );
      // If country is null, default to US
      adjoiningPropertyOwners = adjoiningPropertyOwners?.map((contact) =>
        contact.country === null ? { ...contact, country: 'US' } : contact
      );
      adjoiningPropertyOwners && setRowData(adjoiningPropertyOwners);
      prevRowDataRef.current = adjoiningPropertyOwners;
      isTouched && trigger();
    }, [requestAPIData, isTouched, trigger]);

    useEffect(() => {
      // Clear Adjoining Prop Owners rowData if requestedPermitType is not "Standard"
      requestedPermitType !== 'Standard Permit' && rowData.length !== 0 && setRowData([]);
    }, [requestedPermitType, rowData?.length]);

    useEffect(() => {
      if (rowData) {
        saveContactsTable('adjoiningPropOwners', rowData);
        const requiredFields = {
          firstname: true,
          lastname: true,
          address: true,
          country: true,
          city: true,
          state: true,
          zipcode: 'us',
        };
        const { errorIndices } = validateCSV(rowData, requiredFields);

        errorIndices && setRowErrorIndices(errorIndices);
      }
    }, [rowData, saveContactsTable]);

    useEffect(() => {
      if (isTouched) {
        if (requestedPermitType === 'Standard Permit' && rowData?.length < 1) {
          setError('adjPropertyOwnersTable', {
            type: 'custom',
            message: 'Adjoining Property Owners: At least one adjoining property owner is required',
          });
        } else if (rowErrorIndices.length > 0) {
          setError('adjPropertyOwnersTable', {
            type: 'manual',
            message: 'Adjoining Property Owners: Table contains invalid values.',
          });
        } else {
          trigger('adjPropertyOwnersTable');
        }
      }
    }, [requestedPermitType, rowData, rowErrorIndices, isTouched, setError, trigger]);

    useEffect(() => {
      const validity = errorCount === 0 && rowData?.length > 0;
      updateSection(stepNo, { valid: requestedPermitType === 'Standard Permit' ? validity : true });
    }, [errorCount, rowData, updateSection, stepNo, requestedPermitType, isReadOnly]);

    const handleAddClick = () =>
      openModal(AddAdjoiningPropertyOwnerModal, {
        setRowData,
        rowData,
        isReadOnly,
      });

    const handleAddMultiplePropertyOwnerRows = useCallback(
      (rows, fileName) => {
        const contactsHeaderMap = {
          'first name': 'firstName',
          'middle name': 'middleName',
          'last name': 'lastName',
          address: 'address',
          'address line 2': 'addressTwo',
          city: 'city',
          state: 'state',
          country: 'country',
          'zip code': 'zipcode',
        };

        const formatValue = (key, value) => {
          if (!value) return '';
          switch (key) {
            case 'state': {
              return formatState(value);
            }
            case 'country': {
              return formatCountry(value);
            }
            case 'zip code': {
              return String(value);
            }
            default: {
              return value;
            }
          }
        };

        const requiredFields = {
          'first name': true,
          'last name': true,
          address: true,
          country: true,
          city: true,
          state: true,
          'zip code': 'us',
        };
        const { errors: rowErrors, errorIndices } = validateCSV(rows, requiredFields);

        errorIndices.length > 0 &&
          openSecondaryModal(statusModal, {
            status: 'Import contains missing or invalid data',
            msg: <CsvValidationModal errors={rowErrors} fileName={fileName} />,
          });

        const mappedRows = rows.map((row) => {
          return Object.fromEntries(
            Object.entries(row).map(([key, value]) => {
              return [contactsHeaderMap[key.toLowerCase()] || key.toLowerCase(), formatValue(key.toLowerCase(), value)];
            })
          );
        });

        if (mappedRows.length === 0) {
          return false;
        }

        const mappedRowsWithContactType = mappedRows.map((row) => ({
          ...row,
          contactType: ContactTypes.AdjoiningPropertyOwner,
        }));

        setRowData((oldData) => {
          const newRows = [...oldData, ...mappedRowsWithContactType];
          return newRows;
        });
      },
      [openSecondaryModal]
    );

    const handleFileInput = useCallback(
      async (e) => {
        if (!e.target.files || e.target.files.length === 0) return;
        await handleFileProcessing(async () => {
          const toastId = toast.loading('Processing file(s)...');
          const filesArr = Array.from(e.target.files) ?? [];
          const fileName = e.target.files[0].name;
          setStagedFile(filesArr);
          try {
            const flattenedData = await parseCsvWithXlsx(filesArr);
            const headers = flattenedData[0];
            const tableColumnHeaders = columnDefs?.map((column) => column.header);
            const additionalHeaders = ['address line 2', 'zip code'];
            const columnHeaders = [...tableColumnHeaders, ...additionalHeaders];
            const headersAreValid = await validateCsvHeaders(headers, columnHeaders);
            if (!headersAreValid) {
              openSecondaryModal(statusModal, {
                status: 'Invalid Headers',
                msg: ErrorMessages.InvalidHeaders,
              });
              toast.dismiss(toastId);
              return;
            }
            const csvData = flattenedData.slice(1); // remove headers from flattened data
            const formattedRows = csvData.map((arr) => Object.fromEntries(headers.map((key, i) => [key, arr[i]])));
            const result = handleAddMultiplePropertyOwnerRows(formattedRows, fileName);
            if (result === false) {
              openSecondaryModal(statusModal, {
                status: 'No Valid Records',
                msg: 'No valid Adjoining Property Owner records found in file.',
              });
              toast.dismiss(toastId);
              return;
            }
            tSuccess(toastId, 'File(s) processed successfully.');
          } catch (error) {
            console.error(error);
            tError(toastId, 'An error occurred while processing file(s).');
          } finally {
            setStagedFile([]); //clear the file input to allow another file to be uploaded
          }
        });
      },
      [handleAddMultiplePropertyOwnerRows, openSecondaryModal, columnDefs]
    );

    return (
      <FormProvider {...methods}>
        {errors && isTouched && !isReadOnly && <ErrorSummary errors={errors} sectionNo={stepNo} />}
        <StepHeader text='Adjoining Property Owners'>
          {isStandardPermit ? (
            <>
              <p>
                Provide the names and addresses of all property owners, lessees, etc., whose property adjoins the
                project site and properties that adjoin the waterbody or aquatic site where the work is being proposed.
                This information is used to notify the property owners of the project by Public Notice. Information
                regarding adjoining landowners can typically be obtained through the office of the tax assessor in the
                counties/boroughs where the project is located.
              </p>
              <p>
                Adjoining property owners can be provided in this section by adding them individually or bulk loading
                them using the provided bulk upload CSV.
              </p>
              <div className='margin-top-1 margin-bottom-2'>
                Download CSV Bulk Upload Template:
                <ul>
                  <li>
                    <LinkButton
                      onClick={() => doDownloadFile(ResourceFiles.AdjPropertyOwnerBulkUploadCSV, FileTypes.Resource)}
                      title='Adjoining Property Owner Bulk Upload CSV Template [CSV, 2 KB]'
                      content='Adjoining Property Owner Bulk Upload CSV Template [CSV, 2 KB]'
                    />
                  </li>
                  <ul>
                    <li>Use for uploading adjoining property owners</li>
                  </ul>
                </ul>
              </div>
            </>
          ) : null}
        </StepHeader>
        {!isStandardPermit ? (
          <Alert type='warning' heading='IMPORTANT' headingLevel='h3'>
            Adjoining Property Owners Section required for Standard Permits only.
          </Alert>
        ) : null}

        {isStandardPermit && (
          <>
            <div>
              <FileInput
                accept='.xlsx,.txt,.csv'
                id='adjoining-property-owner-bulk-file'
                name='adjoining-property-owner-bulk-file'
                dragText='Drag .csv or .xlsx file here or '
                aria-label='Upload CSV or XLSX file'
                onChangeCallback={handleFileInput}
                arHeader={true}
                value={stagedFile}
                readOnly={isReadOnly}
              />
            </div>
            <div className='table-button-container'>
              {rowData.length > 0 && (
                <>
                  <Button
                    className='add-property-owner-button'
                    title='Add New Contact'
                    size='small'
                    onClick={() =>
                      openModal(AddAdjoiningPropertyOwnerModal, {
                        setRowData: setRowData,
                        rowData: rowData,
                        isReadOnly: isReadOnly,
                      })
                    }
                    disabled={isReadOnly}
                  >
                    <Icon focusable={false} path={mdiPlusBox} size='16px' />
                    Add New Contact
                  </Button>
                  <Button
                    className={`clear-table-button ${rowData.length === 0 || isReadOnly ? 'disabled' : 'hover'}`}
                    title='Remove All Contacts'
                    size='small'
                    onClick={clearRowData}
                    disabled={isReadOnly || rowData.length < 1}
                  >
                    <Icon focusable={false} path={mdiCloseOctagon} size='16px' />
                    Remove All Contacts
                  </Button>
                </>
              )}
            </div>
            <div className='width-full margin-bottom-2' id='adjPropertyOwnersTable'>
              <TanStackTableBasic
                data={rowData}
                columns={columnDefs}
                placeholderText='No adjoining property owners found.'
                addButtonText='Add New Contact'
                onAddClick={handleAddClick}
                isReadOnly={isReadOnly}
                rowErrorIndices={rowErrorIndices}
              />
            </div>
          </>
        )}
      </FormProvider>
    );
  }
);

PermitsFormAdjoiningPropertyOwners.metadata = PermitsFormAdjoiningPropertyOwnersMetadata;

export default PermitsFormAdjoiningPropertyOwners;
